import { type FormEvent, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { SessionUser } from '@katsquare/types';
import { Button, Card, ErrorText, Field, Input, Spinner } from '../../components/ui';
import { ApiRequestError, login, logout } from '../../lib/api';
import { SESSION_KEY } from '../../features/auth/useSession';

/** Dedicated sign-in for the platform admin account (separate from user login). */
export function AdminLoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [notAdmin, setNotAdmin] = useState(false);

  const mutation = useMutation<SessionUser, ApiRequestError>({
    mutationFn: () => login({ email, password }),
    onSuccess: async (user) => {
      if (!user.isSuperAdmin) {
        // Valid login, but not an admin — drop the session and refuse entry.
        await logout().catch(() => undefined);
        queryClient.setQueryData(SESSION_KEY, null);
        setNotAdmin(true);
        return;
      }
      queryClient.setQueryData(SESSION_KEY, user);
      navigate('/admin', { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setNotAdmin(false);
    mutation.mutate();
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-center gap-2">
          <span className="text-lg font-semibold tracking-tight text-neutral-900">
            KatSquare Draft
          </span>
          <span className="rounded bg-neutral-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
            Admin
          </span>
        </div>
        <Card className="p-6">
          <h1 className="mb-1 text-lg font-semibold text-neutral-900">
            Admin console
          </h1>
          <p className="mb-5 text-sm text-neutral-500">
            Sign in with your platform administrator account.
          </p>
          <form className="flex flex-col gap-4" onSubmit={onSubmit}>
            <Field label="Email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            {notAdmin && <ErrorText>That account isn’t a platform administrator.</ErrorText>}
            {mutation.isError && <ErrorText>{mutation.error.message}</ErrorText>}
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? <Spinner /> : 'Sign in'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
